import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ProcessContainer = styled.div`
  margin: 4rem 0;
`;

const ProcessItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 2.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ProcessNumber = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #0d9488;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  font-size: 1.5rem;
  margin-right: 2rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const ProcessContent = styled.div`
  flex: 1;
`;

const ProcessTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.8rem;
  color: #0d9488;
`;

const ProcessDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const Cleaning: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const processSteps = [
    {
      number: 1,
      title: language === 'it' ? 'ISPEZIONE PRE-ARRIVO' : 'PRE-ARRIVAL INSPECTION',
      description: language === 'it' 
        ? 'Prima dell\'arrivo di ogni ospite, il personale delle pulizie ispeziona attentamente l\'alloggio per identificare eventuali aree che richiedono particolare attenzione o interventi specifici.'
        : 'Before each guest\'s arrival, housekeeping staff carefully inspects the accommodation to identify any areas that require special attention or specific interventions.'
    },
    {
      number: 2,
      title: language === 'it' ? 'PULIZIA PROFONDA' : 'DEEP CLEANING',
      description: language === 'it' 
        ? 'La pulizia copre ogni angolo dell\'alloggio, compresi pavimenti, superfici, piani di lavoro della cucina, bagni e tutte le aree comuni. Particolare attenzione viene prestata ai dettagli come sporco accumulato, macchie o accumuli di polvere.'
        : 'Cleaning covers every corner of the accommodation, including floors, surfaces, kitchen work surfaces, bathrooms and all common areas. Special attention is paid to details such as accumulated dirt, stains or dust accumulation.'
    },
    {
      number: 3,
      title: language === 'it' ? 'CAMBIO BIANCHERIA' : 'LINEN CHANGE',
      description: language === 'it' 
        ? 'La biancheria da letto, gli asciugamani e altri tessuti vengono sostituiti con altri lavati di fresco e accuratamente piegati, garantendo un ambiente fresco e accogliente per ogni ospite.'
        : 'Bed linen, towels and other fabrics are replaced with freshly washed and carefully folded ones, ensuring a fresh and welcoming environment for each guest.'
    },
    {
      number: 4,
      title: language === 'it' ? 'CONTROLLO ARTICOLI DI CORTESIA' : 'CHECK OF COURTESY ITEMS',
      description: language === 'it' 
        ? 'Gli articoli di cortesia come shampoo, gel doccia, carta igienica e altre forniture vengono controllati e riforniti, garantendo che siano sempre disponibili per gli ospiti.'
        : 'Courtesy items such as shampoo, shower gel, toilet paper and other supplies are checked and refilled, ensuring that they are always available for guests.'
    },
    {
      number: 5,
      title: language === 'it' ? 'SANIFICAZIONE SUPERFICI' : 'SURFACE SANITIZATION',
      description: language === 'it' 
        ? 'Vengono utilizzati detergenti efficaci per sanificare tutte le superfici, incluse maniglie delle porte, interruttori della luce e altre aree ad alto contatto per garantire un ambiente sicuro e igienico.'
        : 'Effective cleaners are used to sanitize all surfaces, including door handles, light switches and other high-touch areas to ensure a safe and hygienic environment.'
    },
    {
      number: 6,
      title: language === 'it' ? 'CONTROLLO QUALITÀ' : 'QUALITY CONTROL',
      description: language === 'it' 
        ? 'Un controllo di qualità finale garantisce che ogni dettaglio sia stato curato e che l\'alloggio soddisfi i più alti standard di pulizia. Eventuali dettagli trascurati vengono immediatamente corretti.'
        : 'A final quality control ensures that every detail has been taken care of and that the accommodation meets the highest standards of cleanliness. Any overlooked details are immediately corrected.'
    },
    {
      number: 7,
      title: language === 'it' ? 'PERSONALIZZAZIONE DEL SERVIZIO' : 'SERVICE CUSTOMIZATION',
      description: language === 'it' 
        ? 'Il servizio di pulizia può essere personalizzato in base alle esigenze specifiche della proprietà e alle preferenze degli ospiti. Ad esempio, alcuni ospiti potrebbero richiedere servizi aggiuntivi come la pulizia dei piatti, la pulizia quotidiana dell\'alloggio, il cambio quotidiano della biancheria o l\'organizzazione degli spazi.'
        : 'The cleaning service can be customized based on the specific needs of the property and guest preferences. For example, some guests may request additional services such as cleaning dishes, daily cleaning of the accommodation, daily linen change or organizing spaces.'
    },
    {
      number: 8,
      title: language === 'it' ? 'RAPPORTO DI PULIZIA' : 'CLEANING REPORT',
      description: language === 'it' 
        ? 'Offriamo ai proprietari, host e property manager un rapporto dettagliato sulla pulizia, evidenziando il lavoro svolto e fornendo un registro dell\'attenzione prestata ad ogni pulizia.'
        : 'We offer owners, hosts and property managers a detailed cleaning report, highlighting the work performed and providing a record of the attention given to each cleaning.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Pulizia Alloggi | J-O-H' : 'Accommodation Cleaning | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Servizio professionale di pulizia per alloggi in affitto breve, con attenzione ai dettagli e personalizzazione in base alle esigenze' 
            : 'Professional cleaning service for short-term rental accommodations, with attention to detail and customization based on needs'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/laundry">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'PULIZIA' : 'CLEANING'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'JOLI è il servizio di pulizia perfetto per gli alloggi in affitto a breve termine e si distingue per la sua meticolosità e precisione, garantendo un ambiente impeccabile per ogni ospite.'
              : 'JOLI is the perfect cleaning service for short-term rental accommodation and stands out for its meticulousness and precision, ensuring an impeccable environment for every guest.'}
          </Description>
          
          <ProcessContainer>
            {processSteps.map((step) => (
              <ProcessItem key={step.number}>
                <ProcessNumber>{step.number}</ProcessNumber>
                <ProcessContent>
                  <ProcessTitle>{step.title}</ProcessTitle>
                  <ProcessDescription>{step.description}</ProcessDescription>
                </ProcessContent>
              </ProcessItem>
            ))}
          </ProcessContainer>
          
          <Description>
            {language === 'it' 
              ? 'JOLI va oltre la semplice pulizia di base, garantendo che ogni ospite entri in un ambiente impeccabile, confortevole e igienico. La meticolosità, l\'attenzione ai dettagli e la personalizzazione sono elementi chiave per soddisfare le aspettative degli ospiti e migliorare la reputazione della proprietà.'
              : 'JOLI goes beyond simple basic cleaning, ensuring that every guest enters an impeccable, comfortable and hygienic environment. Meticulousness, attention to detail and personalization are key elements to meeting guest expectations and improving the property\'s reputation.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Richiedi un Preventivo' : 'Request a Quote'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Cleaning; 